import { reaction, type IObservableValue } from 'mobx';
import { PopupHostBridge } from '@cef-ai/wallet';
import type { ApiClient } from '@cef-ai/wallet-api-client';
import type { Identity } from '@cef-ai/wallet-identity';
import { PendingConsentStore, type AppContextStore, type ApplicationsStore, type DelegationsStore } from '~/stores';
import { getFirstPartyOrigins } from '~/config';
import {
  createHelloHandler,
  createSignHandler,
  createFindApplicationsHandler,
  createSaveApplicationHandler,
  createRequestDelegationHandler,
  createLogoutHandler,
  createSessionStatusHandler,
} from '~/popup/handlers';

export interface CreateIframeBridgeOptions {
  /** Origin of the host page (read from the iframe URL's `?origin=...` query). */
  hostOrigin: string;
  /** Optional window override for tests. Defaults to `globalThis.window`. */
  popupWindow?: Window;
  identity: Identity;
  apiClient: ApiClient;
  appContextStore: AppContextStore;
  applicationsStore: ApplicationsStore;
  delegationsStore: DelegationsStore;
  /**
   * Optional injection point — production callers can omit this and the
   * bridge constructs its own. Tests pass a pre-built one to inspect/drive.
   */
  pendingConsentStore?: PendingConsentStore;
  /**
   * Optional second overlay-visibility input, owned by the caller (Task 7's
   * login-gesture "Continue" button). When provided, the `wallet:ui:*`
   * reaction watches `pendingConsentStore.pending !== null || loginGesturePending.get()`
   * instead of just the consent flag, so this factory remains the SINGLE
   * emitter of `wallet:ui:show`/`wallet:ui:hide` — callers must never send
   * those messages directly, or the host overlay can flicker/hide out from
   * under a still-pending state (consent or gesture).
   */
  loginGesturePending?: IObservableValue<boolean>;
  /**
   * Issue #36. The route's boot-time silent-resume attempt, hoisted out of its
   * `useEffect` so the `wallet:session:status` handler can await it before
   * answering — see `createSessionStatusHandler`'s `bootResume` doc for the full
   * race and for why awaiting it does not weaken the "never triggers WebAuthn"
   * invariant.
   *
   * Must already exist when this factory runs, which is why `EmbedWalletRoute`
   * builds it during render rather than inside an effect: the bridge effect runs
   * FIRST (it is declared first), so an effect-created promise would not exist
   * yet at this call.
   *
   * Optional — omitted by unit tests that drive the bridge directly, in which
   * case the handler answers immediately, exactly as it did pre-#36.
   */
  bootResume?: Promise<unknown>;
}

export interface CreateIframeBridgeResult {
  bridge: PopupHostBridge;
  pendingConsentStore: PendingConsentStore;
  /**
   * Release auxiliary resources owned by the bridge wiring (the cross-tab
   * auth reaction and the `wallet:ui` overlay reaction). Call this AFTER
   * `bridge.stop()` on unmount.
   */
  dispose: () => void;
}

/**
 * Construct a fully-wired iframe-side bridge. Same wiring as
 * `createPopupBridge` (Spec §5 transport + §6.2 routes), with two
 * differences required when the embed route is framed rather than opened as
 * a popup (Task 3 / Task 7):
 *
 *   1. The underlying `PopupHostBridge` uses `replyTo: 'parent'` so replies
 *      go to `window.parent.postMessage` instead of `window.opener`.
 *   2. A `wallet:ui:show` / `wallet:ui:hide` signal is emitted whenever a
 *      consent prompt opens/closes, so the host-side `IframeTransport` can
 *      show its modal overlay exactly while a prompt is up (the framed
 *      wallet document itself is invisible/zero-size until then).
 *
 * Caller responsibilities: identical to `createPopupBridge` — pass the right
 * `hostOrigin`, call `bridge.start(...)`, and on unmount call `bridge.stop()`
 * then `dispose()`.
 */
export function createIframeBridge(opts: CreateIframeBridgeOptions): CreateIframeBridgeResult {
  const bridge = new PopupHostBridge({
    hostOrigin: opts.hostOrigin,
    popupWindow: opts.popupWindow,
    replyTo: 'parent',
  });
  const send = (msg: any) => bridge.send(msg);
  const pendingConsentStore = opts.pendingConsentStore ?? new PendingConsentStore();
  // The wallet SPA's own origin — the popup/iframe is served from the wallet
  // origin, so `location.origin` here IS the wallet origin. Used by the
  // requestDelegation handler for its first-party (same-site) consent default.
  const walletOrigin = (globalThis as any).location?.origin ?? '';
  // #74 — the set of origins that auto-approve delegation is explicit
  // per-environment config, not a guess derived from the wallet's hostname.
  const firstPartyOrigins = getFirstPartyOrigins();
  const deps = {
    identity: opts.identity,
    apiClient: opts.apiClient,
    appContextStore: opts.appContextStore,
    applicationsStore: opts.applicationsStore,
    delegationsStore: opts.delegationsStore,
    pendingConsentStore,
    walletOrigin,
    firstPartyOrigins,
    send,
    // Issue #36 — consumed only by createSessionStatusHandler; every other
    // handler ignores it.
    bootResume: opts.bootResume,
  };
  bridge.on('wallet:hello', createHelloHandler(deps));
  bridge.on('wallet:sign', createSignHandler(deps));
  bridge.on('wallet:findApplications', createFindApplicationsHandler(deps));
  bridge.on('wallet:saveApplication', createSaveApplicationHandler(deps));
  bridge.on('wallet:requestDelegation', createRequestDelegationHandler(deps));
  bridge.on('wallet:logout', createLogoutHandler(deps));
  // Task 20: the reply's accountId/email/displayName ride `deps.identity` --
  // same object the handler already reads addresses/credentialId off. No
  // extra wiring needed here beyond passing identity through, same as before.
  bridge.on('wallet:session:status', createSessionStatusHandler(deps));

  // Cross-tab logout dismisses any in-flight consent prompt. See
  // `createPopupBridge` for the full rationale — identical here.
  const disposeAuthReaction = reaction(
    () => opts.identity.isAuthenticated,
    (isAuth, wasAuth) => {
      if (wasAuth && !isAuth && pendingConsentStore.pending) {
        pendingConsentStore.resolveCurrent({ kind: 'deny' });
      }
    },
  );

  // Iframe-only: the framed wallet document has no visible chrome of its own
  // until the host shows its modal overlay. Tell the host exactly when to
  // show/hide it by mirroring a SINGLE combined visibility expression as
  // `wallet:ui:*` — this is the only place in the codebase that sends those
  // message types, so there is exactly one source of truth for the overlay.
  // The expression ORs together every reason the overlay should be up: a
  // pending consent prompt, and (when the caller passes one) the Task 7
  // login-gesture flag while the "Continue" button is showing. Adding a
  // future third reason means adding a term here, not a new emitter.
  const disposeUiReaction = reaction(
    () => pendingConsentStore.pending !== null || (opts.loginGesturePending?.get() ?? false),
    (visible) => {
      bridge.send(
        visible
          ? { type: 'wallet:ui:show', id: crypto.randomUUID() }
          : { type: 'wallet:ui:hide', id: crypto.randomUUID() },
      );
    },
  );

  return {
    bridge,
    pendingConsentStore,
    dispose: () => {
      disposeAuthReaction();
      disposeUiReaction();
    },
  };
}
